// src/components/NotificationBell.jsx
import { Badge, Spinner } from "flowbite-react";
import { AnimatePresence, motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import useNotifications from "../hooks/useNotifications";
import { alertDialog } from "../utils/dialogs";
import {
  BellIcon,
  CheckCircleIcon,
  CloseIcon,
  stripLeadingEmoji,
} from "./common/NotificationIcons";

/**
 * Responsivo:
 * - Mobile (default): sheet fullscreen (fixed, inset-0), header com fechar, conteúdo scrollável.
 * - ≥ sm: popover posicionado (absolute) com largura fixa.
 * A11y:
 * - Botão com aria-controls/expanded.
 * - Fecha com ESC e clique fora.
 * UX:
 * - Bloqueia scroll do body no mobile quando aberto.
 */

export default function NotificationBell() {
  const {
    isLoading,
    items,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const [attentionPulse, setAttentionPulse] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const prevUnreadRef = useRef(unreadCount || 0);
  const pulseTimerRef = useRef(null);
  const dateFormatter = useRef(
    new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    })
  );

  const formatDateTime = (value) => {
    if (!value) return null;
    let dateValue = value;
    if (typeof value === "string" && /^\d+$/.test(value)) {
      dateValue = Number(value);
    }
    if (typeof dateValue === "number" && dateValue < 1_000_000_000_000) {
      dateValue = dateValue * 1000;
    }
    const dt = new Date(dateValue);
    if (Number.isNaN(dt.getTime())) return null;
    return dateFormatter.current.format(dt);
  };

  const handleMarkAsRead = async (n) => {
    if (n.isRead) return;
    try {
      await markAsRead(n.id);
    } catch {
      alertDialog({
        title: "Não foi possível marcar como lida",
        message: "Tente novamente em instantes.",
        tone: "error",
      });
    }
  };

  const handleDelete = async (n) => {
    try {
      await deleteNotification(n.id);
    } catch {
      alertDialog({
        title: "Não foi possível excluir",
        message: "A notificação não foi excluída. Tente novamente em instantes.",
        tone: "error",
      });
    }
  };

  // fechar ao clicar fora (apenas em viewport >= sm; no mobile é fullscreen)
  useEffect(() => {
    const onClick = (e) => {
      const isSmall = window.matchMedia("(min-width: 640px)").matches; // sm
      if (!isSmall) return;
      if (
        open &&
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // fechar com ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // bloquear scroll no mobile quando aberto
  useEffect(() => {
    const isSmall = !window.matchMedia("(min-width: 640px)").matches;
    if (open && isSmall) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Realça o sino quando chegam novas notificações não lidas.
  useEffect(() => {
    const prevUnread = Number(prevUnreadRef.current || 0);
    const currentUnread = Number(unreadCount || 0);
    const hasNewUnread = currentUnread > prevUnread;
    prevUnreadRef.current = currentUnread;

    if (!hasNewUnread) return;
    setAttentionPulse(true);
    if (pulseTimerRef.current) {
      clearTimeout(pulseTimerRef.current);
    }
    pulseTimerRef.current = window.setTimeout(() => {
      setAttentionPulse(false);
      pulseTimerRef.current = null;
    }, 2600);

    return () => {
      if (pulseTimerRef.current) {
        clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = null;
      }
    };
  }, [unreadCount]);

  const hasUnread = unreadCount > 0;
  const shouldHighlight = hasUnread && !open;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        className="rs-bell-btn relative p-2 rounded-full hover:bg-black/5 transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-300"
        title="Notificações"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="notification-panel"
      >
        {shouldHighlight && (
          <motion.span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full bg-orange-400/20"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: [0.95, 1.35], opacity: [0.45, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut" }}
          />
        )}
        <motion.span
          className="relative block"
          animate={
            shouldHighlight
              ? { rotate: [0, -12, 10, -8, 0], scale: [1, 1.06, 1] }
              : { rotate: 0, scale: 1 }
          }
          transition={
            shouldHighlight
              ? { duration: 1.4, repeat: Infinity, repeatDelay: 1.1 }
              : { duration: 0.2 }
          }
        >
          <BellIcon className="rs-bell-icon w-6 h-6" />
        </motion.span>
        {unreadCount > 0 && (
          <motion.span
            className="absolute top-0 right-0"
            animate={attentionPulse ? { scale: [1, 1.24, 1] } : { scale: 1 }}
            transition={attentionPulse ? { duration: 0.45, repeat: 3 } : { duration: 0.2 }}
          >
            <Badge className="rs-bell-badge rounded-full px-2 py-0.5 text-2xs bg-orange-500 text-white shadow-md shadow-orange-500/40">
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          </motion.span>
        )}
      </button>
      {/* Painel responsivo */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop (só mobile) */}
            <motion.div
              className="fixed inset-0 bg-black/20 sm:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            {/* Container: mobile = fullscreen; ≥sm = popover */}
            <motion.div
              id="notification-panel"
              role="dialog"
              aria-label="Notificações"
              aria-modal="true"
              ref={panelRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.14 }}
              className={[
                // mobile fullscreen sheet
                "fixed inset-0 z-50 sm:z-auto sm:static sm:inset-auto",
                "sm:absolute sm:right-0 sm:mt-2",
                "w-screen h-screen sm:w-[380px] sm:max-w-[92vw] sm:h-auto",
                "rounded-none sm:rounded-2xl overflow-hidden",
                "border-0 sm:border sm:border-slate-200",
                "rs-bell-panel bg-white shadow-none sm:shadow-xl",
              ].join(" ")}
            >
              {/* Header: mobile tem botão de fechar; desktop tem ação à direita */}
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="font-semibold text-gray-700">Notificações</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => markAllAsRead()}
                    className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border text-gray-600 hover:bg-gray-50"
                  >
                    <CheckCircleIcon className="text-green-600" />
                    Marcar todas como lidas
                  </button>
                  <button
                    onClick={() => setOpen(false)}
                    className="sm:hidden inline-flex p-2 rounded-md hover:bg-slate-100"
                    aria-label="Fechar"
                  >
                    <CloseIcon className="h-5 w-5 text-slate-600" />
                  </button>
                </div>
              </div>

              {/* Lista */}
              <div className="rs-bell-list max-h-[calc(100vh-48px)] sm:max-h-[60vh] overflow-auto">
                {isLoading ? (
                  <div className="p-4 flex items-center gap-2 text-sm text-slate-500">
                    <Spinner size="sm" /> Carregando...
                  </div>
                ) : items.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">
                    Nenhuma notificação.
                  </div>
                ) : (
                  <ul className="flex flex-col gap-[5px]">
                    {items.map((n) => {
                      const createdAt =
                        n?.createdAt ?? n?.created_at ?? n?.date ?? n?.timestamp;
                      const createdAtLabel = formatDateTime(createdAt);
                      const openTo =
                        n.url ||
                        (n.entityType === "card" && n.entityId
                          ? `/crm?cardId=${n.entityId}`
                          : null);
                      return (
                        <li
                          key={n.id}
                          className={`rs-notif-card px-3 pt-3 pb-3 sm:px-3 sm:pt-2 ${
                            !n.isRead ? "bg-orange-50/60" : "bg-white"
                          }`}
                        >
                          {/* Linha de cima: data e "x" (excluir) à direita */}
                          <div className="flex items-center justify-end gap-2">
                            {createdAtLabel && (
                              <span className="rs-notif-date text-[10px] leading-none text-slate-400">
                                {createdAtLabel}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDelete(n)}
                              className="rs-notif-delete inline-flex rounded p-0.5 text-slate-400 hover:text-slate-600"
                              title="Excluir notificação"
                              aria-label="Excluir notificação"
                            >
                              <CloseIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="mt-1 min-w-0 text-left">
                            <div className="text-sm font-medium truncate text-gray-600">
                              {!n.isRead ? (
                                <strong>{stripLeadingEmoji(n.title)}</strong>
                              ) : (
                                stripLeadingEmoji(n.title)
                              )}
                            </div>
                            {n.body && (
                              <div className="text-xs text-gray-600 line-clamp-2">
                                {n.body}
                              </div>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center justify-start gap-2">
                            {openTo && (
                              <Link
                                to={openTo}
                                onClick={() => handleMarkAsRead(n)}
                                className="rs-notif-btn rs-notif-btn--open"
                              >
                                Abrir
                              </Link>
                            )}
                            {!n.isRead && (
                              <button
                                type="button"
                                onClick={() => handleMarkAsRead(n)}
                                className="rs-notif-btn rs-notif-btn--muted"
                              >
                                Marcar como lida
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
